import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Edit, Trash2, CheckCircle, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

interface ScheduleItemCardProps {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  eventType: string;
  status: string;
  recurrence: string | null;
  onEdit: () => void;
  onDelete: () => void;
  onComplete: () => void;
}

const eventTypeConfig = {
  session: {
    color: "bg-purple-500",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    textColor: "text-purple-700 dark:text-purple-400",
    label: "Session"
  },
  meeting: {
    color: "bg-blue-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    textColor: "text-blue-700 dark:text-blue-400",
    label: "Meeting"
  },
  task: {
    color: "bg-green-500",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
    textColor: "text-green-700 dark:text-green-400",
    label: "Task"
  },
  reminder: {
    color: "bg-orange-500",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    textColor: "text-orange-700 dark:text-orange-400",
    label: "Reminder"
  }
};

export const ScheduleItemCard = ({
  title,
  description,
  startTime,
  endTime,
  eventType,
  status,
  recurrence,
  onEdit,
  onDelete,
  onComplete,
}: ScheduleItemCardProps) => {
  const config = eventTypeConfig[eventType as keyof typeof eventTypeConfig] || eventTypeConfig.task;
  const isCompleted = status === "completed";

  return (
    <div
      className={cn(
        "group relative flex items-start gap-4 p-4 rounded-lg border transition-all duration-300",
        isCompleted ? "opacity-60" : "hover:shadow-md",
        config.bgColor,
        config.borderColor
      )}
    >
      {/* Color indicator strip */}
      <div className={cn("w-1 h-full absolute left-0 top-0 bottom-0 rounded-l-lg", config.color)} />

      {/* Time badge */}
      <div className="flex flex-col items-center min-w-[80px] pt-1">
        <Badge variant="secondary" className="mb-1 text-xs font-medium">
          <Clock className="w-3 h-3 mr-1" />
          {new Date(startTime).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {new Date(endTime).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className={cn("font-semibold text-base", isCompleted && "line-through")}>
            {title}
          </h3>
          <Badge variant="outline" className={cn("text-xs capitalize", config.textColor)}>
            {config.label}
          </Badge>
        </div>
        
        {description && (
          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{description}</p>
        )}
        
        <div className="flex items-center gap-2 flex-wrap">
          {recurrence && (
            <Badge variant="secondary" className="text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              {recurrence}
            </Badge>
          )}
          {isCompleted && (
            <Badge variant="default" className="text-xs bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              Completed
            </Badge>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!isCompleted && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onComplete}
            className="h-8 w-8 hover:bg-green-500/20"
            title="Mark as completed"
          >
            <CheckCircle className="w-4 h-4" />
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          onClick={onEdit}
          className="h-8 w-8"
          title="Edit"
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={onDelete}
          className="h-8 w-8 hover:bg-destructive/20"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
